import { hint } from "@i18n";
import { Select } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";

export type Option = { value: string; label: string };

/** A picker's list wider than a narrow field (a side panel's): as wide as its longest hit, within these bounds. */
export const WIDE_LIST = { width: "max-content", position: "bottom-start" } as const;
export const WIDE_LIST_STYLE = { minWidth: 320, maxWidth: 560 };

/** A record picked by typing: ``onQuery`` gets the text a moment after typing stops ("" when there is nothing to
 *  search: the chosen record's label is shown in the field, it is not searched for), ``options`` are its hits; the
 *  chosen record keeps its ``current`` label while the hits move on. */
export function SearchSelect(props: {
  value: number | null;
  onChange: (id: number | null) => void;
  onQuery: (q: string) => void;
  options: Option[];
  current: string | null;
  label?: string;
  placeholder?: string;
  minSearchLength?: number;
  w?: number;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const { onQuery, options, current } = props;
  const [text, setText] = useState("");
  const [typed] = useDebouncedValue(text.trim(), 300);
  const [shown, setShown] = useState<string | null>(null); // the chosen option's label, as the field shows it
  const query = typed === shown || typed === current ? "" : typed;
  useEffect(() => {
    onQuery(query);
  }, [query, onQuery]);
  const value = props.value != null ? String(props.value) : null;
  const data = useMemo(() => {
    const known = value != null && !options.some((o) => o.value === value) ? [{ value, label: current ?? value }] : [];
    return [...known, ...options];
  }, [options, value, current]);
  return (
    <Select
      size="xs"
      w={props.w}
      label={props.label}
      placeholder={props.placeholder ?? hint("search")}
      aria-label={props.label ? undefined : props.placeholder}
      withAsterisk={props.required}
      disabled={props.disabled}
      error={props.error}
      searchable
      // the guidance is in the list (not a description squeezed under a narrow field), the list wider than the field
      nothingFoundMessage={
        query.length < (props.minSearchLength ?? 1) ? hint(props.minSearchLength === 2 ? "searchTwoHelp" : "searchHelp") : hint("noResults")
      }
      comboboxProps={WIDE_LIST}
      styles={{ dropdown: WIDE_LIST_STYLE }}
      clearable
      allowDeselect={false}
      searchValue={text}
      onSearchChange={setText}
      value={value}
      onChange={(v, option) => {
        setShown(option?.label ?? null);
        props.onChange(v ? Number(v) : null);
      }}
      data={data}
      filter={({ options: all }) => all}
    />
  );
}
